import { useState, useEffect } from "react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { createYear, activateYear, updateYear, deleteYear } from "@/lib/api";
import { toast } from "sonner";
import { Calendar, ChevronDown, Plus, Check, Star, Settings2, Trash2, Save } from "lucide-react";

const suggestNext = (years) => {
  const labels = years.map((y) => y.label).filter((l) => /^\d{4}/.test(l));
  const maxStart = labels.reduce((m, l) => Math.max(m, parseInt(l.slice(0, 4), 10)), 0);
  const start = maxStart || new Date().getFullYear();
  return `${start + 1}\u2013${start + 2}`;
};

const inputCls = "rounded-none border-[#0A1F44]/30 bg-transparent focus-visible:ring-1 focus-visible:ring-[#C0982E] font-mono text-sm";

export const YearSelector = ({ years, current, onSelect, onChanged, isAdmin }) => {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [manageOpen, setManageOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState("");
  const [confirmDel, setConfirmDel] = useState("");

  useEffect(() => {
    if (manageOpen) {
      setRows(years.map((y) => ({ orig: y.label, label: y.label, sheet_url: y.sheet_url || "", active: y.active, count: y.count })));
      setConfirmDel("");
    }
  }, [manageOpen, years]);

  const openNew = () => { setLabel(suggestNext(years)); setOpen(true); };

  const create = async () => {
    const clean = label.trim();
    if (!clean) { toast.error("Enter a year label"); return; }
    try {
      await createYear(clean);
      toast.success(`Started ${clean}`);
      setOpen(false);
      onSelect(clean);
      onChanged();
    } catch { toast.error("Could not create year"); }
  };

  const makeCurrent = async (yr) => {
    try {
      await activateYear(yr);
      toast.success(`${yr} set as current`);
      onChanged();
    } catch { toast.error("Failed"); }
  };

  const setRow = (i, patch) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));

  const saveRow = async (row) => {
    const newLabel = row.label.trim();
    if (!newLabel) { toast.error("Year label cannot be empty"); return; }
    setBusy(row.orig);
    try {
      await updateYear(row.orig, newLabel, row.sheet_url.trim());
      toast.success(`Saved ${newLabel}`);
      if (current === row.orig && newLabel !== row.orig) onSelect(newLabel);
      onChanged();
      setRows((r) => r.map((x) => (x.orig === row.orig ? { ...x, orig: newLabel, label: newLabel } : x)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save changes");
    } finally { setBusy(""); }
  };

  const removeRow = async (row) => {
    setBusy(row.orig);
    try {
      const res = await deleteYear(row.orig);
      toast.success(`Removed ${row.orig}${res.removed_staff ? ` and ${res.removed_staff} staff` : ""}`);
      const remaining = rows.filter((x) => x.orig !== row.orig);
      setRows(remaining);
      if (current === row.orig) onSelect(remaining[0]?.orig || "");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete year");
    } finally { setBusy(""); setConfirmDel(""); }
  };

  const activateInline = async (row) => {
    setBusy(row.orig);
    try {
      await activateYear(row.orig);
      toast.success(`${row.orig} set as current`);
      setRows((r) => r.map((x) => ({ ...x, active: x.orig === row.orig })));
      onChanged();
    } catch { toast.error("Failed"); } finally { setBusy(""); }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="year-selector"
            className="flex items-center gap-2 border border-[#0A1F44]/30 px-3 py-2 text-xs uppercase tracking-wider text-[#0A1F44] hover:bg-[#E6EAF1] transition-colors"
          >
            <Calendar className="w-4 h-4 text-[#C0982E]" strokeWidth={1.5} />
            {current || "Year"}
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="rounded-none border border-[#0A1F44] bg-[#F3F5F8] w-64">
          {years.map((y) => (
            <DropdownMenuItem
              key={y.label}
              data-testid={`year-option-${y.label}`}
              onClick={() => onSelect(y.label)}
              className="rounded-none flex items-center justify-between gap-2 cursor-pointer focus:bg-[#E6EAF1]"
            >
              <span className="flex items-center gap-2">
                {y.label === current ? <Check className="w-3.5 h-3.5 text-[#C0982E]" /> : <span className="w-3.5" />}
                {y.label}
                {y.active && <Star className="w-3 h-3 text-[#C0982E] fill-[#C0982E]" />}
              </span>
              <span className="text-[10px] text-[#0A1F44]/50">{y.count}</span>
            </DropdownMenuItem>
          ))}
          {isAdmin && current && years.find((y) => y.label === current && !y.active) && (
            <DropdownMenuItem
              data-testid="year-make-current"
              onClick={() => makeCurrent(current)}
              className="rounded-none text-xs text-[#0A1F44]/70 focus:bg-[#E6EAF1] cursor-pointer"
            >
              <Star className="w-3.5 h-3.5 mr-2" /> Set {current} as current
            </DropdownMenuItem>
          )}
          {isAdmin && (
            <>
              <DropdownMenuSeparator className="bg-[#0A1F44]/15" />
              <DropdownMenuItem
                data-testid="new-year-btn"
                onClick={openNew}
                className="rounded-none text-[#C0982E] font-medium focus:bg-[#E6EAF1] cursor-pointer"
              >
                <Plus className="w-4 h-4 mr-2" /> New School Year
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="manage-years-btn"
                onClick={() => setManageOpen(true)}
                className="rounded-none text-[#0A1F44]/80 focus:bg-[#E6EAF1] cursor-pointer"
              >
                <Settings2 className="w-4 h-4 mr-2" /> Manage Years
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* New year */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="new-year-dialog" className="rounded-none border border-[#0A1F44] bg-[#F3F5F8] max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#0A1F44]">New School Year</DialogTitle>
            <DialogDescription className="text-[#0A1F44]/60">
              Creates a fresh, empty roster and makes it the current year. Import your updated
              sheet next to populate it — past years stay available.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs uppercase tracking-wider">Year Label</Label>
            <Input
              data-testid="new-year-input"
              className={inputCls}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="2026–2027"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none border-[#0A1F44]" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="new-year-create" className="rounded-none bg-[#0A1F44] text-[#F3F5F8] hover:bg-[#C0982E]" onClick={create}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage years */}
      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent data-testid="manage-years-dialog" className="rounded-none border border-[#0A1F44] bg-[#F3F5F8] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#0A1F44] flex items-center gap-2">
              <Settings2 className="w-5 h-5 text-[#C0982E]" /> Manage Years
            </DialogTitle>
            <DialogDescription className="text-[#0A1F44]/60">
              Rename a year, update its linked Google Sheet, set the current year, or delete a year.
              Deleting a year permanently removes its staff records.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {rows.map((row, i) => (
              <div key={row.orig} data-testid={`manage-year-row-${row.orig}`} className="border border-[#0A1F44]/20 p-4 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 flex-1">
                    <Label className="text-[10px] uppercase tracking-wider text-[#0A1F44]/50 w-14 shrink-0">Label</Label>
                    <Input
                      data-testid={`manage-year-label-${row.orig}`}
                      className={inputCls}
                      value={row.label}
                      onChange={(e) => setRow(i, { label: e.target.value })}
                    />
                  </div>
                  <span className="text-[10px] uppercase tracking-wider text-[#0A1F44]/50 shrink-0">{row.count} staff</span>
                  {row.active ? (
                    <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#C0982E] shrink-0">
                      <Star className="w-3 h-3 fill-[#C0982E]" /> Current
                    </span>
                  ) : (
                    <button
                      data-testid={`manage-year-activate-${row.orig}`}
                      onClick={() => activateInline(row)}
                      disabled={busy === row.orig}
                      className="text-[10px] uppercase tracking-wider text-[#0A1F44]/60 hover:text-[#C0982E] shrink-0"
                    >
                      Set current
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <Label className="text-[10px] uppercase tracking-wider text-[#0A1F44]/50 w-14 shrink-0">Sheet</Label>
                  <Input
                    data-testid={`manage-year-sheet-${row.orig}`}
                    className={inputCls}
                    value={row.sheet_url}
                    onChange={(e) => setRow(i, { sheet_url: e.target.value })}
                    placeholder="Google Sheets link this year syncs from"
                  />
                </div>

                <div className="flex justify-between items-center pt-1">
                  {confirmDel === row.orig ? (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#b3402a]">Delete {row.orig} & its staff?</span>
                      <Button data-testid={`manage-year-delete-confirm-${row.orig}`} onClick={() => removeRow(row)} disabled={busy === row.orig} className="rounded-none bg-[#b3402a] hover:bg-[#8f3221] text-white h-8 text-xs">Confirm</Button>
                      <Button variant="outline" onClick={() => setConfirmDel("")} className="rounded-none border-[#0A1F44]/40 h-8 text-xs">Cancel</Button>
                    </div>
                  ) : (
                    <button
                      data-testid={`manage-year-delete-${row.orig}`}
                      onClick={() => setConfirmDel(row.orig)}
                      className="flex items-center gap-1.5 text-xs text-[#b3402a] hover:underline"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete year
                    </button>
                  )}
                  <Button
                    data-testid={`manage-year-save-${row.orig}`}
                    onClick={() => saveRow(row)}
                    disabled={busy === row.orig}
                    className="rounded-none bg-[#0A1F44] text-[#F3F5F8] hover:bg-[#C0982E] hover:text-[#0A1F44] gap-1.5 h-8 text-xs uppercase tracking-wider transition-colors"
                  >
                    <Save className="w-3.5 h-3.5" /> Save
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button data-testid="manage-years-new" variant="outline" className="rounded-none border-[#C0982E] text-[#C0982E] gap-1.5" onClick={() => { setManageOpen(false); openNew(); }}>
              <Plus className="w-4 h-4" /> New Year
            </Button>
            <Button className="rounded-none bg-[#0A1F44] text-[#F3F5F8] hover:bg-[#C0982E]" onClick={() => setManageOpen(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
